import { useCallback, useMemo, useState } from 'react';
import { History, Clock, CheckCircle, XCircle, ChevronDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { useResponsiveToolbar } from '../../../components/ui/responsive-toolbar';
import { useQueryHistoryStore, RECENT_HISTORY_COUNT } from '../store/queryHistoryStore';
import type { QueryHistoryEntry } from '../types/queryHistoryTypes';
import { getQueryHistoryPreview } from '../utils/queryHistoryPreview';

const EMPTY_ENTRIES: QueryHistoryEntry[] = [];

interface RecentQueriesDropdownProps {
  /** Connection ID for filtering history */
  connectionId?: string;
  /** Database context for filtering history */
  database?: string;
  /** Called when user selects a query from history */
  onSelectQuery: (sql: string) => void;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
}

/**
 * Format relative time (e.g., "2m ago", "1h ago")
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function HistoryItem({
  entry,
  onSelect,
}: {
  entry: QueryHistoryEntry;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full px-3 py-2 text-left hover:bg-accent transition-colors flex items-start gap-2 group border-b border-border/30 last:border-b-0"
    >
      {/* Status icon */}
      <span className="flex-shrink-0 mt-0.5">
        {entry.success ? (
          <CheckCircle className="h-3.5 w-3.5 text-green-500/70" />
        ) : (
          <XCircle className="h-3.5 w-3.5 text-red-500/70" />
        )}
      </span>

      {/* Query text */}
      <span className="flex-1 min-w-0 text-xs font-mono text-muted-foreground group-hover:text-foreground truncate">
        {getQueryHistoryPreview(entry.sql)}
      </span>

      {/* Time ago */}
      <span className="flex-shrink-0 text-[10px] text-muted-foreground/60 flex items-center gap-1">
        <Clock className="h-2.5 w-2.5" />
        {formatRelativeTime(entry.executedAt)}
      </span>
    </button>
  );
}

export function RecentQueriesDropdown({
  connectionId,
  database,
  onSelectQuery,
  disabled = false,
}: RecentQueriesDropdownProps) {
  const { leftCollapseState } = useResponsiveToolbar();
  const isCollapsed = leftCollapseState === 'collapsed';
  const [isOpen, setIsOpen] = useState(false);
  const entriesForContext = useQueryHistoryStore((state) => {
    if (!connectionId) return EMPTY_ENTRIES;
    return state.getEntriesForContext(connectionId, database);
  });

  const recentEntries = useMemo(
    () => entriesForContext.slice(0, RECENT_HISTORY_COUNT),
    [entriesForContext]
  );

  const handleSelect = useCallback(
    (entry: QueryHistoryEntry) => {
      onSelectQuery(entry.sql);
      setIsOpen(false);
    },
    [onSelectQuery]
  );

  const hasEntries = recentEntries.length > 0;
  const isDisabled = disabled || !connectionId;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {/* Ghost (PP-03 QE toolbar hierarchy) — steps down alongside its
            Plan/Result siblings so Execute is the only strong control. */}
        <Button
          size="sm"
          variant="ghost"
          disabled={isDisabled}
          title={
            isDisabled
              ? 'Connect to a database to see recent queries'
              : hasEntries
                ? 'Recent queries'
                : 'No recent queries'
          }
          data-testid="recent-queries-button"
          data-toolbar-item
          data-toolbar-left-collapsible
          className="px-2"
        >
          <History className="h-4 w-4" />
          {!isCollapsed && 'Recent'}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0 !z-[9999]">
        {/* Header */}
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border bg-muted/30">
          <History className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Recent Queries
          </span>
        </div>

        {/* Content */}
        {hasEntries ? (
          <div className="max-h-[300px] overflow-y-auto" data-testid="recent-queries-list">
            {recentEntries.map((entry) => (
              <HistoryItem
                key={entry.id}
                entry={entry}
                onSelect={() => handleSelect(entry)}
              />
            ))}
          </div>
        ) : (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground" data-testid="recent-queries-empty">
            No recent queries for this connection
          </div>
        )}

        {/* Footer hint */}
        {hasEntries && (
          <div className="px-3 py-1.5 border-t border-border/30 bg-muted/20">
            <span className="text-[10px] text-muted-foreground/60">
              Click to insert query into editor
            </span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
